import React, { useMemo, Fragment, ReactElement } from 'react';
import Table from '../../../components/table/table.component';
import TableBody from '../../../components/table-body/table-body.component';
import TableRow from '../../../components/table-row/table-row.component';
import TableCell from '../../../components/table-cell/table-cell.component';
import RequiredMark from '../../../components/required-mark/required-mark.component';
import Tooltip from '../../../components/tooltip/tooltip.component';
import ChipLink from '../../../components/chip-link/chip-link.component';
import Hint from '../../../components/hint/hint.component';
import ActionButton from '../../../components/action-button/action-button.component';
import FlexContainer from '../../../components/flex-container/flex-container.component';
import FormFieldSelect from '../../../components/form-field-select/form-field-select.component';
import FormFieldMultiSelect from '../../../components/form-field-multi-select/form-field-multi-select.component';
import FormFieldText from '../../../components/form-field-text/form-field-text.component';
import FormFieldNumber from '../../../components/form-field-number/form-field-number.component';
import MenuItemAddNewItem from '../../../components/menu-item-add-new-item/menu-item-add-new-item.component';
import TableRowAddNewItem from '../../../components/table-row-add-new-item/table-row-add-new-item.component';
import MenuItemList from '../../../components/menu-item-list/menu-item-list.component';
import SinglePageTab from '../../app/common/single-page-tab/single-page-tab.component';
import RemoveIcon from '../../../icons/remove-icon';
import CancelFilledIcon from '../../../icons/cancel-filled-icon';
import StructureService from '../../../services/structure-service';
import FormService from '../../../services/form-service';
import { CONTACT_CREATE, CONTACT_VIEW } from '../../../constants/entity/permissions';
import CompanyDefaultImage from '../../../static/images/company-default-image.png';

const AssetOther: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const COLUMNS_TOTAL: number = 2;

  const {
    isSaving,
    isSaveDisabled,
    isEditMode,
    isNewSellerVisible,
    isRightDrawerOpen,
    assetData,
    contactCompanyList,
    assetOwnershipStructureTypeList,
    selectedSellers,
    assetFieldItems,
    assetFieldNewItemIds,
    formError,
    onSellersChange,
    onContactClick,
    onOpenNewSellerForm,
    onCloseNewSellerForm,
    onAssetFieldAddNewItem,
    onAssetFieldDeleteClick,
    onCancelForm,
    onSubmitForm,
    onContactCompanySearch,
    onContactCompanySearchClose,
  } = props;

  const formPrefix: string = 'assetData';

  const isEmpty: boolean = !isEditMode && !assetData.loanAmount && !assetData.ltv && !assetData.assetOwnershipStructure
    && !assetData.cost && !assetData.occupancy && !assetData.currentNoi && !assetData.ttmNoi && !assetData.runRateNoi
    && !assetData.forwardNoi && (!assetData.sellers || !assetData.sellers.length) && (!assetData.lastDeal
      || ((!assetData.lastDeal.lenders || !assetData.lastDeal.lenders.length) && !assetData.sponsor))
    && !assetData.portfolioInvestedEquity && !assetData.loanNumber && !assetData.yardiLoanBalance
    && !assetData.yardiSeniorLoan && !assetData.yardiMarketValueLoan && !assetData.yardiLoanClose
    && !assetData.yardiLoanMaturity && !assetData.yardiLoanCommitment && !assetData.yardiLoanContribution
    && !assetData.yardiUnfundedLoan && !assetData.yardiCurrentInterest && !assetData.yardiItdInterest
    && !assetData.yardiCurrentAmortization && !assetData.yardiItdAmortization && !assetData.yardiIrrOnLoan
    && !assetData.yardiEquityMultiple && !assetData.yardiGainOnLoan && !assetData.yardiLtv
    && !assetData.yardiCurrentSofr && !assetData.yardiLoanSpread && !assetData.yardiCurrentRate
    && !assetData.yardiFirstExtensionTerm && !assetData.yardiSecondExtensionTerm && !assetData.yardiBorrower
    && !assetData.yardiSponsor && !assetData.yardiSponsorAdditional
    && (!assetFieldItems || !Object.keys(assetFieldItems).length);

  const assetOwnershipStructureTypeMap: Record<string, AssetOwnershipStructureType>
    = useMemo((): Record<string, AssetOwnershipStructureType> => (
    StructureService.getItemMap(assetOwnershipStructureTypeList, 'name')
  ), [assetOwnershipStructureTypeList]);

  const menuItemAssetOwnershipStructureTypeList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    optionItemValueKey: 'name',
    optionList: assetOwnershipStructureTypeList,
  }), [assetOwnershipStructureTypeList]);

  const menuItemContactCompanySellerList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    withImage: true,
    isExtended: true,
    optionItemLabelKey: 'fullName',
    optionList: contactCompanyList,
    defaultImage: CompanyDefaultImage,
    selectedItemList: selectedSellers,
  }), [contactCompanyList, selectedSellers]);

  return (
    <SinglePageTab
      isSaving={isSaving}
      isSaveDisabled={isSaveDisabled}
      isEditMode={isEditMode}
      isEmpty={isEmpty}
      isRightDrawerOpen={isRightDrawerOpen}
      formError={formError}
      onCancelForm={onCancelForm}
      onSubmitForm={onSubmitForm}
    >
      <Table>
        <TableBody isPlain withNoBorders withTwoColumns withHeaderColumn>
          {
            (isEditMode || assetData.loanAmount) && (
              <TableRow>
                <TableCell>
                  Loan Amount
                </TableCell>
                <TableCell>
                  {
                    isEditMode ? (
                      <FormFieldNumber isBordered name={`${formPrefix}.loanAmount`} />
                    ) : assetData.loanAmount
                  }
                </TableCell>
              </TableRow>
            )
          }
          {
            (!isEditMode && assetData.ltv) && (
              <TableRow>
                <TableCell>
                  LTV
                </TableCell>
                <TableCell>
                  {assetData.ltv}
                </TableCell>
              </TableRow>
            )
          }
          {
            (isEditMode || assetData.assetOwnershipStructure) && (
              <TableRow>
                <TableCell>
                  Ownership Structure
                </TableCell>
                <TableCell>
                  {
                    isEditMode ? (
                      <FormFieldSelect
                        isBordered
                        withBlankElement
                        isDisabled={assetOwnershipStructureTypeList && !assetOwnershipStructureTypeList.length}
                        name={`${formPrefix}.assetOwnershipStructure`}
                        optionsTotal={assetOwnershipStructureTypeList && assetOwnershipStructureTypeList.length}
                      >
                        {menuItemAssetOwnershipStructureTypeList}
                      </FormFieldSelect>
                    ) : (
                      (assetOwnershipStructureTypeMap[assetData.assetOwnershipStructure]
                        && assetOwnershipStructureTypeMap[assetData.assetOwnershipStructure].title) || ''
                    )
                  }
                </TableCell>
              </TableRow>
            )
          }
          {
            (isEditMode || assetData.cost) && (
              <TableRow>
                <TableCell>
                  Cost
                </TableCell>
                <TableCell>
                  {
                    isEditMode ? (
                      <FormFieldNumber isBordered name={`${formPrefix}.cost`} />
                    ) : assetData.cost
                  }
                </TableCell>
              </TableRow>
            )
          }
          {
            (isEditMode || assetData.occupancy) && (
              <TableRow>
                <TableCell>
                  Occupancy
                </TableCell>
                <TableCell>
                  {
                    isEditMode ? (
                      <FormFieldText isBordered name={`${formPrefix}.occupancy`} />
                    ) : assetData.occupancy
                  }
                </TableCell>
              </TableRow>
            )
          }
          {
            (isEditMode || assetData.currentNoi) && (
              <TableRow>
                <TableCell>
                  Current NOI
                </TableCell>
                <TableCell>
                  {
                    isEditMode ? (
                      <FormFieldNumber isBordered name={`${formPrefix}.currentNoi`} />
                    ) : assetData.currentNoi
                  }
                </TableCell>
              </TableRow>
            )
          }
          {
            (isEditMode || assetData.ttmNoi) && (
              <TableRow>
                <TableCell>
                  TTM NOI
                </TableCell>
                <TableCell>
                  {
                    isEditMode ? (
                      <FormFieldNumber isBordered name={`${formPrefix}.ttmNoi`} />
                    ) : assetData.ttmNoi
                  }
                </TableCell>
              </TableRow>
            )
          }
          {
            (isEditMode || assetData.runRateNoi) && (
              <TableRow>
                <TableCell>
                  Run Rate NOI
                </TableCell>
                <TableCell>
                  {
                    isEditMode ? (
                      <FormFieldNumber isBordered name={`${formPrefix}.runRateNoi`} />
                    ) : assetData.runRateNoi
                  }
                </TableCell>
              </TableRow>
            )
          }
          {
            (isEditMode || assetData.forwardNoi) && (
              <TableRow>
                <TableCell>
                  Forward NOI
                </TableCell>
                <TableCell>
                  {
                    isEditMode ? (
                      <FormFieldNumber isBordered name={`${formPrefix}.forwardNoi`} />
                    ) : assetData.forwardNoi
                  }
                </TableCell>
              </TableRow>
            )
          }
          {
            (isEditMode || (assetData.sellers && !!assetData.sellers.length)) && (
              <TableRow>
                <TableCell>
                  Sellers
                </TableCell>
                <TableCell>
                  {
                    isEditMode ? (
                      <FormFieldMultiSelect
                        isBordered
                        withImage
                        withSearch
                        isDisabled={contactCompanyList && !contactCompanyList.length}
                        name={`${formPrefix}.sellerIds`}
                        defaultImage={CompanyDefaultImage}
                        optionList={contactCompanyList}
                        onSearch={onContactCompanySearch}
                        onClose={onContactCompanySearchClose}
                        onValuesChange={onSellersChange}
                        newItemComponent={(
                          !isNewSellerVisible && (
                            <MenuItemAddNewItem
                              label="New Contact"
                              createPermission={CONTACT_CREATE}
                              onClick={onOpenNewSellerForm}
                            />
                          )
                        )}
                      >
                        {menuItemContactCompanySellerList}
                      </FormFieldMultiSelect>
                    ) : (
                      assetData.sellers && !!assetData.sellers.length
                      && assetData.sellers.map((contactItem: Contact): ReactElement => (
                        <ChipLink
                          isDisabled={isEditMode}
                          key={contactItem.id}
                          label={contactItem.fullName}
                          imageLink={contactItem.photoThumbnail || contactItem.photo || CompanyDefaultImage}
                          permission={CONTACT_VIEW}
                          onClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
                            onContactClick(event, contactItem.id);
                          }}
                        />
                      ))
                    )
                  }
                </TableCell>
              </TableRow>
            )
          }
          {
            isEditMode && isNewSellerVisible && (
              <TableRow>
                <TableCell>
                  Seller (new)
                </TableCell>
                <TableCell isTextRight>
                  <ActionButton onClick={onCloseNewSellerForm}>
                    <CancelFilledIcon />
                  </ActionButton>
                </TableCell>
              </TableRow>
            )
          }
          {
            isEditMode && isNewSellerVisible && (
              <TableRow>
                <TableCell>
                  Seller Name
                  <RequiredMark />
                </TableCell>
                <TableCell>
                  <FormFieldText isBordered withAutoFocus={isNewSellerVisible} name={`${formPrefix}.seller.name`} />
                </TableCell>
              </TableRow>
            )
          }
          {
            isEditMode && isNewSellerVisible && (
              <TableRow>
                <TableCell>
                  Seller Website
                  <Hint
                    title={'Fill in Website field to search and automatically populate other fields '
                      + 'of the company, if it is listed in our dictionary'}
                  />
                </TableCell>
                <TableCell>
                  <FormFieldText
                    isBordered
                    name={`${formPrefix}.seller.website`}
                  />
                </TableCell>
              </TableRow>
            )
          }
          {
            !isEditMode && assetData.lastDeal && assetData.lastDeal.lenders
            && !!assetData.lastDeal.lenders.length && (
              <TableRow>
                <TableCell>
                  Lender
                </TableCell>
                <TableCell>
                  {
                    assetData.lastDeal.lenders.map((contactItem: Contact): ReactElement => (
                      <ChipLink
                        isDisabled={isEditMode}
                        key={contactItem.id}
                        label={contactItem.fullName}
                        imageLink={contactItem.photoThumbnail || contactItem.photo || CompanyDefaultImage}
                        permission={CONTACT_VIEW}
                        onClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
                          onContactClick(event, contactItem.id);
                        }}
                      />
                    ))
                  }
                </TableCell>
              </TableRow>
            )
          }
          {
            (isEditMode || assetData.portfolioInvestedEquity) && (
              <TableRow>
                <TableCell>
                  Portfolio Invested Equity
                </TableCell>
                <TableCell>
                  {
                    isEditMode ? (
                      <FormFieldNumber isBordered name={`${formPrefix}.portfolioInvestedEquity`} />
                    ) : assetData.portfolioInvestedEquity
                  }
                </TableCell>
              </TableRow>
            )
          }
          {
            (isEditMode || assetData.loanNumber) && (
              <TableRow>
                <TableCell>
                  Loan Number
                </TableCell>
                <TableCell>
                  {
                    isEditMode ? (
                      <FormFieldText isBordered name={`${formPrefix}.loanNumber`} />
                    ) : assetData.loanNumber
                  }
                </TableCell>
              </TableRow>
            )
          }
          {
            !isEditMode && !!assetData.lastDeal && assetData.lastDeal.sponsor && (
              <TableRow>
                <TableCell>
                  Deal Owner
                </TableCell>
                <TableCell>
                  {assetData.lastDeal.sponsor.fullName}
                </TableCell>
              </TableRow>
            )
          }
          {
            !isEditMode && assetData.yardiLoanBalance && (
              <TableRow>
                <TableCell>
                  Loan Balance (Yardi)
                </TableCell>
                <TableCell>
                  {assetData.yardiLoanBalance}
                </TableCell>
              </TableRow>
            )
          }
          {
            !isEditMode && assetData.yardiSeniorLoan && (
              <TableRow>
                <TableCell>
                  Senior Loan (Yardi)
                </TableCell>
                <TableCell>
                  {assetData.yardiSeniorLoan}
                </TableCell>
              </TableRow>
            )
          }
          {
            !isEditMode && assetData.yardiMarketValueLoan && (
              <TableRow>
                <TableCell>
                  Market Value Loan (Yardi)
                </TableCell>
                <TableCell>
                  {assetData.yardiMarketValueLoan}
                </TableCell>
              </TableRow>
            )
          }
          {
            !isEditMode && assetData.yardiLoanClose && (
              <TableRow>
                <TableCell>
                  Loan Close (Yardi)
                </TableCell>
                <TableCell>
                  {assetData.yardiLoanClose}
                </TableCell>
              </TableRow>
            )
          }
          {
            !isEditMode && assetData.yardiLoanMaturity && (
              <TableRow>
                <TableCell>
                  Loan Maturity (Yardi)
                </TableCell>
                <TableCell>
                  {assetData.yardiLoanMaturity}
                </TableCell>
              </TableRow>
            )
          }
          {
            !isEditMode && assetData.yardiLoanCommitment && (
              <TableRow>
                <TableCell>
                  Loan Commitment (Yardi)
                </TableCell>
                <TableCell>
                  {assetData.yardiLoanCommitment}
                </TableCell>
              </TableRow>
            )
          }
          {
            !isEditMode && assetData.yardiLoanContribution && (
              <TableRow>
                <TableCell>
                  Loan Contribution (Yardi)
                </TableCell>
                <TableCell>
                  {assetData.yardiLoanContribution}
                </TableCell>
              </TableRow>
            )
          }
          {
            !isEditMode && assetData.yardiUnfundedLoan && (
              <TableRow>
                <TableCell>
                  Unfunded Loan (Yardi)
                </TableCell>
                <TableCell>
                  {assetData.yardiUnfundedLoan}
                </TableCell>
              </TableRow>
            )
          }
          {
            !isEditMode && assetData.yardiCurrentInterest && (
              <TableRow>
                <TableCell>
                  Current Interest (Yardi)
                </TableCell>
                <TableCell>
                  {assetData.yardiCurrentInterest}
                </TableCell>
              </TableRow>
            )
          }
          {
            !isEditMode && assetData.yardiItdInterest && (
              <TableRow>
                <TableCell>
                  ITD Interest (Yardi)
                </TableCell>
                <TableCell>
                  {assetData.yardiItdInterest}
                </TableCell>
              </TableRow>
            )
          }
          {
            !isEditMode && assetData.yardiCurrentAmortization && (
              <TableRow>
                <TableCell>
                  Current Amortization (Yardi)
                </TableCell>
                <TableCell>
                  {assetData.yardiCurrentAmortization}
                </TableCell>
              </TableRow>
            )
          }
          {
            !isEditMode && assetData.yardiItdAmortization && (
              <TableRow>
                <TableCell>
                  ITD Amortization (Yardi)
                </TableCell>
                <TableCell>
                  {assetData.yardiItdAmortization}
                </TableCell>
              </TableRow>
            )
          }
          {
            !isEditMode && assetData.yardiIrrOnLoan && (
              <TableRow>
                <TableCell>
                  IRR on Loan (Yardi)
                </TableCell>
                <TableCell>
                  {assetData.yardiIrrOnLoan}
                </TableCell>
              </TableRow>
            )
          }
          {
            !isEditMode && assetData.yardiEquityMultiple && (
              <TableRow>
                <TableCell>
                  Equity Multiple (Yardi)
                </TableCell>
                <TableCell>
                  {assetData.yardiEquityMultiple}
                </TableCell>
              </TableRow>
            )
          }
          {
            !isEditMode && assetData.yardiGainOnLoan && (
              <TableRow>
                <TableCell>
                  Gain on Loan (Yardi)
                </TableCell>
                <TableCell>
                  {assetData.yardiGainOnLoan}
                </TableCell>
              </TableRow>
            )
          }
          {
            !isEditMode && assetData.yardiLtv && (
              <TableRow>
                <TableCell>
                  LTV (Yardi)
                </TableCell>
                <TableCell>
                  {assetData.yardiLtv}
                </TableCell>
              </TableRow>
            )
          }
          {
            !isEditMode && assetData.yardiCurrentSofr && (
              <TableRow>
                <TableCell>
                  Current SOFR (Yardi)
                </TableCell>
                <TableCell>
                  {assetData.yardiCurrentSofr}
                </TableCell>
              </TableRow>
            )
          }
          {
            !isEditMode && assetData.yardiLoanSpread && (
              <TableRow>
                <TableCell>
                  Loan Spread (Yardi)
                </TableCell>
                <TableCell>
                  {assetData.yardiLoanSpread}
                </TableCell>
              </TableRow>
            )
          }
          {
            !isEditMode && assetData.yardiCurrentRate && (
              <TableRow>
                <TableCell>
                  Current Rate (Yardi)
                </TableCell>
                <TableCell>
                  {assetData.yardiCurrentRate}
                </TableCell>
              </TableRow>
            )
          }
          {
            !isEditMode && assetData.yardiFirstExtensionTerm && (
              <TableRow>
                <TableCell>
                  1st Extension Term (Yardi)
                </TableCell>
                <TableCell>
                  {assetData.yardiFirstExtensionTerm}
                </TableCell>
              </TableRow>
            )
          }
          {
            !isEditMode && assetData.yardiSecondExtensionTerm && (
              <TableRow>
                <TableCell>
                  2nd Extension Term (Yardi)
                </TableCell>
                <TableCell>
                  {assetData.yardiSecondExtensionTerm}
                </TableCell>
              </TableRow>
            )
          }
          {
            !isEditMode && assetData.yardiBorrower && (
              <TableRow>
                <TableCell>
                  Borrower (Yardi)
                </TableCell>
                <TableCell>
                  {assetData.yardiBorrower}
                </TableCell>
              </TableRow>
            )
          }
          {
            !isEditMode && assetData.yardiSponsor && (
              <TableRow>
                <TableCell>
                  Sponsor (Yardi)
                </TableCell>
                <TableCell>
                  {assetData.yardiSponsor}
                </TableCell>
              </TableRow>
            )
          }
          {
            !isEditMode && assetData.yardiSponsorAdditional && (
              <TableRow>
                <TableCell>
                  2nd Sponsor (Yardi)
                </TableCell>
                <TableCell>
                  {assetData.yardiSponsorAdditional}
                </TableCell>
              </TableRow>
            )
          }
          {
            ((assetFieldItems && Object.values(assetFieldItems)) || [])
              .map((assetFieldItem: AssetField): ReactElement => {
                const isNewItem: boolean = assetFieldNewItemIds && assetFieldNewItemIds.length
                  ? assetFieldNewItemIds.includes(assetFieldItem.id) : false;
                const formKeyAssetField: string = FormService.getIndexedFormFieldKey(assetFieldItem.id);

                return (
                  <TableRow isActive={isNewItem} key={assetFieldItem.id}>
                    <TableCell>
                      {
                        isEditMode ? (
                          <Fragment>
                            <FormFieldText
                              isHidden
                              name={`${formPrefix}.options[${formKeyAssetField}].id`}
                            />
                            <FormFieldText
                              isBordered
                              name={`${formPrefix}.options[${formKeyAssetField}].name`}
                              placeholder="Name"
                            />
                          </Fragment>
                        ) : assetFieldItem.name
                      }
                    </TableCell>
                    <TableCell>
                      {
                        isEditMode ? (
                          <FlexContainer>
                            <FormFieldText
                              isBordered
                              name={`${formPrefix}.options[${formKeyAssetField}].value`}
                              placeholder="Value"
                            />
                            <Tooltip title="Delete">
                              <ActionButton
                                isInline
                                withOffset
                                withNoPadding
                                onClick={(): void => {
                                  onAssetFieldDeleteClick(assetFieldItem.id);
                                }}
                              >
                                <RemoveIcon />
                              </ActionButton>
                            </Tooltip>
                          </FlexContainer>
                        ) : assetFieldItem.value
                      }
                    </TableCell>
                  </TableRow>
                );
              })
          }
          {
            isEditMode && (
              <TableRowAddNewItem colSpan={COLUMNS_TOTAL + 1} onClick={onAssetFieldAddNewItem} />
            )
          }
        </TableBody>
      </Table>
    </SinglePageTab>
  );
});

export default AssetOther;
